import { call, put, select, takeEvery, takeLatest } from 'typed-redux-saga'
import { RequestParams, ResponseCatalog } from 'Models'
import {
	errorHandler,
	NOTIFICATION_TYPE,
	showNotificationAction,
	NotificationPopupFooterComponent,
	notificationPopupSlice,
	notifyMessageCreator,
	NotificationPopupModel,
	ResponseError,
} from 'Modules'
import { Deserialize } from 'cerialize'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import { i18n } from 'LibNeo'
import {
	BgRequestNs,
	bgRequestSlice,
	bgRequestSelector,
	bgRequestApi,
	getBgIdFromLocation,
	AST_ERROR_KEYS,
	AST_ERROR_CODE,
	DEFAULT_ERROR_PARAMS,
	prepareErrors,
	BankGuarantee,
	isIbroRequestsPage,
	handleErrorWithForms,
	RejectDescr,
} from 'Pages/ibro'
import { sagaCallbackChannel } from 'Store'
import { SocketMessageTypes } from 'Modules/Socket'

function* updateStatus(action: BgRequestNs.UpdateStatus) {
	try {
		const { id, status, request_type, errors = [], changes } = action.payload
		const currentBgId = getBgIdFromLocation(FrontUrls.broker.index_neo)

		if (status) {
			if (request_type !== SocketMessageTypes.CHECK_STATUS || changes?.status) {
				const data = [id.toString()]
				yield* call(notifyMessageCreator, 'broker.update_status.success', { data })
			}
		} else {
			const astErrors = errors?.filter((error) => {
				if (error instanceof Object)
					return Object.keys(error).some((key) => AST_ERROR_KEYS.includes(key as AST_ERROR_CODE))
				return []
			})

			if (currentBgId === id && !astErrors?.length) {
				yield* call(
					handleErrorWithForms,
					Deserialize(
						{
							data: {
								errors: errors || [],
							},
							...DEFAULT_ERROR_PARAMS,
						},
						ResponseError
					)
				)
			} else {
				yield* put(
					showNotificationAction({
						type: NOTIFICATION_TYPE.ERROR,
						prepareErrors: prepareErrors(errors),
					})
				)
			}
		}

		if (currentBgId === id) {
			yield* put(bgRequestSlice.actions.getItem(id))
		} else if (
			(request_type === SocketMessageTypes.CHECK_STATUS && changes?.status) ||
			(request_type !== SocketMessageTypes.CHECK_STATUS && status)
		) {
			const bgList = yield* select(bgRequestSelector.selectItems)
			const findIndex = bgList?.findIndex((bg) => bg.id === id)
			if (findIndex + 1) {
				const newBg = yield* call(bgRequestApi.getBg, id)
				const updatedBgList = Object.assign(new ResponseCatalog<BankGuarantee>(), bgList, {
					entries: bgList.map(
						(bg): BankGuarantee => (bg.id === id ? Deserialize(newBg, BankGuarantee) : bg)
					),
				})
				yield* put(bgRequestSlice.actions.setList(updatedBgList))
			}
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* setCalculateCommission(action: BgRequestNs.SetCalculateCommission) {
	try {
		const { status, change, params, errors = [] } = action.payload
		const currentBgId = getBgIdFromLocation(FrontUrls.broker.index_neo)

		if (status) {
			yield* put(bgRequestSlice.actions.changeSocket({ change, params }))
			yield* put(bgRequestSlice.actions.setCommissionReject(false))
		} else if (
			!change.request_id &&
			errors.some((error) => {
				if (error instanceof Object)
					return Object.keys(error).includes(AST_ERROR_CODE.REJECT_DESCRIPTION)
				return false
			})
		) {
			yield* put(bgRequestSlice.actions.changeSocket({ change: { ...change, fee: 0 }, params }))
		}
		if (
			!status &&
			errors.some((error) => {
				if (error instanceof Object)
					return error[AST_ERROR_CODE.REJECT_DESCRIPTION] === RejectDescr.NPA
				return false
			}) &&
			currentBgId === change.request_id
		) {
			yield* put(bgRequestSlice.actions.setCommissionReject(true))
		}
		yield* put(bgRequestSlice.actions.setAwaitingCalculateCommission(false))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}
function* confirmedChangeSendAddress(action: BgRequestNs.ConfirmedChangeSendAddress) {
	try {
		const { id, status } = action.payload
		const currentBg = yield* select(bgRequestSelector.selectCurrentBg)
		if (currentBg?.id === id) yield* put(bgRequestSlice.actions.getItem(id))
		if (!status) {
			const notify: NotificationPopupModel = {
				type: NOTIFICATION_TYPE.ERROR,
				title: 'broker.change_send_address_error',
				close: true,
				footer: NotificationPopupFooterComponent({
					title: 'broker.open_request',
					action: () =>
						sagaCallbackChannel.put(
							routerActions.push(FrontUrls.broker.requests_edit.replace(':id', id.toString()))
						),
				}),
			}
			yield* put(notificationPopupSlice.actions.add(notify))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* cloneRequestComplete(action: BgRequestNs.CloneRequestComplete) {
	try {
		const { clone_request_id, id } = action.payload
		if (isIbroRequestsPage()) {
			const params = yield* select(bgRequestSelector.selectParams)
			const paramsForRequest = Object.assign(new RequestParams(), params, { page: 1 })
			yield* put(bgRequestSlice.actions.getList({ params: paramsForRequest }))
		}
		if (clone_request_id) {
			const notify: NotificationPopupModel = {
				type: NOTIFICATION_TYPE.INFO,
				title: `${i18n('broker.clone_request_created')} ${id}`,
				close: true,
				footer: NotificationPopupFooterComponent({
					title: 'broker.open_request',
					action: () =>
						sagaCallbackChannel.put(
							routerActions.push(
								FrontUrls.broker.requests_edit.replace(':id', clone_request_id.toString())
							)
						),
				}),
			}
			yield* put(notificationPopupSlice.actions.add(notify))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* bgRequestSocketSaga() {
	yield* takeLatest(bgRequestSlice.actions.updateStatusItem.type, updateStatus)
	yield* takeEvery(bgRequestSlice.actions.setCalculateCommission.type, setCalculateCommission)
	yield* takeLatest(
		bgRequestSlice.actions.confirmedChangeSendAddress.type,
		confirmedChangeSendAddress
	)
	yield* takeLatest(bgRequestSlice.actions.cloneRequestComplete.type, cloneRequestComplete)
}
